import { useNotifications } from '@armhr/ui';
import {
  DescriptionOutlined,
  Download,
  FactCheckOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import PdfReader from '../common/PdfReader';

const CSA_PDF_FILENAME = 'Updated-CSA-Terms-and-Conditions.pdf';

const stepLabelSx = {
  color: 'primary.main',
  fontWeight: 600,
  letterSpacing: 1,
} as const;

const CsaAcknowledgementPrompt: React.FC = () => {
  const api = useApi();
  const { company } = useUser();
  const { showNotification } = useNotifications();
  const [dialogOpen, setDialogOpen] = useState(true);
  const [step, setStep] = useState(1);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [reachedBottom, setReachedBottom] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: csaAck, refresh } = useApiData((api) =>
    api.company.getCsaAcknowledgement()
  );

  const clientName = company?.name ?? 'the Client';
  const effectiveDate = format(new Date(), 'MMMM d, yyyy');
  const acknowledgementText = `I acknowledge and agree, on behalf of ${clientName}, that Schedule C to the Client Services Agreement between ${clientName} and Armhr, LLC is replaced in its entirety by the updated Schedule C linked above, effective ${effectiveDate}. I confirm that I am authorized to bind ${clientName} to this amendment.`;

  const checkScroll = useCallback(() => {
    if (!contentRef.current) return;

    const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) {
      setScrollProgress(0);
      return;
    }

    const progress = Math.min((scrollTop / maxScroll) * 100, 100);
    setScrollProgress(progress);
    if (maxScroll - scrollTop < 40) {
      setReachedBottom(true);
    }
  }, []);

  const resetReviewState = useCallback(() => {
    setReachedBottom(false);
    setScrollProgress(0);
    contentRef.current?.scrollTo(0, 0);
  }, []);

  const handleClose = () => {
    setDialogOpen(false);
    setStep(1);
    setAgreed(false);
    resetReviewState();
  };

  const handleAcknowledge = async () => {
    try {
      setSubmitting(true);
      await api.company.acknowledgeCsa();
      showNotification({
        message:
          'Your CSA acknowledgement has been successfully recorded! Our team will reach out if any further action is required.',
        severity: 'success',
      });
      await refresh();
      handleClose();
    } catch {
      showNotification({
        message:
          'Unable to record your acknowledgement. Please try again later.',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = CSA_PDF_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (step !== 2) return;

    let cancelled = false;
    resetReviewState();

    async function loadPdf() {
      const blob = await api.company.downloadCsaTerms();
      if (cancelled) return;
      setPdfUrl(URL.createObjectURL(blob));
    }

    loadPdf();

    return () => {
      cancelled = true;
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      resetReviewState();
    };
  }, [step, api, resetReviewState]);

  useEffect(() => {
    if (step !== 2 || !pdfUrl) return;
    resetReviewState();
  }, [step, pdfUrl, resetReviewState]);

  useEffect(() => {
    const el = contentRef.current;
    if (step !== 2 || !el || reachedBottom) return;
    const observer = new ResizeObserver(() => checkScroll());
    observer.observe(el);
    return () => observer.disconnect();
  }, [step, reachedBottom, checkScroll, pdfUrl]);

  if (!csaAck || csaAck.acknowledged) {
    return null;
  }

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      maxWidth={step === 1 ? 'sm' : step === 2 ? 'lg' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          ...(step === 2
            ? {
                height: '85vh',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
              }
            : {}),
        },
      }}
    >
      {step === 1 && (
        <Box>
          <DialogTitle sx={{ pt: 3.5, pb: 1, px: 3 }}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                }}
              >
                <DescriptionOutlined sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ ...stepLabelSx, display: 'block', mb: 0.75 }}
                >
                  Action required
                </Typography>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 700, lineHeight: 1.3 }}
                >
                  Updated Client Services Agreement
                </Typography>
              </Box>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 1 }}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: 'center', lineHeight: 1.6 }}
              >
                Armhr is updating the terms and conditions of our service.
                <br />
                Please review the revised Client Services Agreement and
                acknowledge this change on behalf of your organization.
              </Typography>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', lineHeight: 1.55 }}
                >
                  This is a one-time acknowledgement so Armhr can apply these
                  updates going forward <br /> without collecting additional
                  confirmations for this change.
                </Typography>
              </Box>
            </Stack>
          </DialogContent>
        </Box>
      )}
      {step === 2 && (
        <>
          <DialogTitle
            sx={{
              flexShrink: 0,
              px: 3,
              py: 2,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{ ...stepLabelSx, display: 'block', mb: 0.25 }}
                >
                  Document review
                </Typography>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 700, lineHeight: 1.3 }}
                >
                  Updated terms & conditions
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, lineHeight: 1.5 }}
                >
                  Scroll through the full document before continuing. You can
                  download a copy for your records at any time.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={handleDownload}
                disabled={!pdfUrl}
                sx={{ flexShrink: 0 }}
              >
                Download PDF
              </Button>
            </Stack>
          </DialogTitle>
          <DialogContent
            ref={contentRef}
            onScroll={checkScroll}
            sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}
          >
            <Slide
              direction="left"
              in={step === 2}
              mountOnEnter
              unmountOnExit
              timeout={{ enter: 400, exit: 400 }}
              container={contentRef.current}
            >
              <Box sx={{ width: '100%' }}>
                {pdfUrl ? (
                  <PdfReader pdfUrl={pdfUrl} pageWidth={500} />
                ) : (
                  <Stack alignItems="center" spacing={1.5} sx={{ my: 6 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                      Loading updated terms…
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Slide>
          </DialogContent>
        </>
      )}
      {step === 3 && (
        <Slide
          direction="left"
          in={step === 3}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 400, exit: 400 }}
        >
          <Box>
            <DialogTitle sx={{ pt: 3.5, pb: 1, px: 3 }}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    color: 'primary.main',
                  }}
                >
                  <FactCheckOutlined sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ ...stepLabelSx, display: 'block', mb: 0.75 }}
                  >
                    Final step
                  </Typography>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 700, lineHeight: 1.3 }}
                  >
                    Acknowledge the updated terms
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 1 }}>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textAlign: 'center', lineHeight: 1.6 }}
                >
                  Confirm that you have reviewed the updated Client Services
                  Agreement and
                  <br /> are authorized to acknowledge this change for{' '}
                  {clientName}.
                </Typography>
                <FormControlLabel
                  sx={{
                    alignItems: 'flex-start',
                    mx: 0,
                    gap: 1.5,
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: agreed ? 'primary.main' : 'grey.200',
                    transition: 'border-color 0.2s ease',
                  }}
                  control={
                    <Checkbox
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      color="primary"
                      sx={{ pt: 0.25 }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {acknowledgementText}
                    </Typography>
                  }
                />
              </Stack>
            </DialogContent>
          </Box>
        </Slide>
      )}
      <DialogActions
        sx={
          step === 2
            ? {
                flexShrink: 0,
                px: 3,
                py: 2,
                gap: 2,
                borderTop: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'grey.50',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
              }
            : {
                px: 3,
                py: 2.5,
                gap: 1,
              }
        }
      >
        {step === 2 && (
          <Stack spacing={0.75} sx={{ minWidth: 200 }}>
            <Typography
              variant="caption"
              sx={{
                color: reachedBottom ? 'success.dark' : 'text.secondary',
                fontWeight: 600,
              }}
            >
              {reachedBottom
                ? 'Review complete — you may continue'
                : 'Scroll to the end to continue'}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 180, maxWidth: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={reachedBottom ? 100 : scrollProgress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      backgroundColor: reachedBottom
                        ? 'success.main'
                        : 'primary.main',
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  minWidth: 36,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              >
                {reachedBottom ? '100%' : `${Math.round(scrollProgress)}%`}
              </Typography>
            </Stack>
          </Stack>
        )}

        <Stack direction="row" gap={1} sx={{ ml: step === 2 ? 'auto' : 0 }}>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>

          {step === 1 && (
            <Button
              startIcon={<DescriptionOutlined />}
              variant="contained"
              onClick={() => setStep(2)}
            >
              Review updates
            </Button>
          )}

          {step === 2 && (
            <>
              <Button variant="outlined" onClick={() => setStep(1)}>
                Back
              </Button>
              <Tooltip
                placement="top"
                title={
                  !reachedBottom
                    ? 'Please scroll through the entire document first'
                    : null
                }
              >
                <div>
                  <Button
                    variant="contained"
                    disabled={!reachedBottom}
                    onClick={() => {
                      setAgreed(false);
                      setStep(3);
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </Tooltip>
            </>
          )}

          {step === 3 && (
            <>
              <Button variant="outlined" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!agreed || submitting}
                onClick={handleAcknowledge}
              >
                {submitting ? 'Submitting…' : 'Submit acknowledgement'}
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CsaAcknowledgementPrompt;
